import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    FiCheckboxDisabledFn,
    IFiCheckboxChangeEvent,
    IFiCheckBoxChangeEvent,
    IFiCheckboxItem,
} from '@fi-shared/fi-checkbox2/fi-checkbox2';
import { isDefined } from '@fi-shared/utils';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
    selector: 'fi-checkbox2',
    template: ` <div class="fi-checkbox-main">
              @if (hasChildItems() && !isHeader(item)) {
                <span
                  (click)="checkboxToggleClicked()"
                  class="ToggleIcon"
                  [class.MinusIcon]="isShowChildren()"
                  [class.PlusIcon]="!isShowChildren()"
                  >
                </span>
                @if (hasChildItems()) {
                  <span class="ToggleDash"></span>
                }
              }
            
              @if (isChild && !isHeader(item)) {
                <span
                  [class.DottedHasChildItems]="hasChildItems()"
                  [class.DottedNonChildItems]="!hasChildItems() && !parentIsHeader"
                ></span>
              }
            
              <mat-checkbox
                [class.Disabled]="disabled"
                [ngClass]="item._class"
                [checked]="checked"
                [indeterminate]="indeterminate"
                (change)="onCheckedChange($event)"
                [disabled]="disabled"
                class="BrandedCheckbox"
                aria-label="{{ item.name }}"
                title="{{ getTitle(item) }}"
                >
                <span [class.bold]="isHeader(item)">{{ item.name }}</span>
                <!--<b>{{item.id}}</b>&nbsp;t:{{idx}}&nbsp;{{checked}}&nbsp;{{indeterminate}}-->
                <ng-content></ng-content>
              </mat-checkbox>
            </div>
            
            @if (hasChildItems()) {
              <div
                class="CcmCheckBoxListChildWrap"
                [class.ShowChildren]="isShowChildren()"
                [class.HideChildren]="!isShowChildren()"
                >
                <div class="CcmCheckBoxListChildBorder"></div>
                @for (childItem of item.childItems; track childItem) {
                  <fi-checkbox2
                    [class.itemParent]="isHeader(item)"
                    [item]="childItem"
                    [ngModel]="ngModel"
                    [disableIds]="disableIds"
                    [isCheckboxDisabledFn]="isCheckboxDisabledFn"
                    [canChangeChecked]="canChangeChecked"
                    (checkedChange)="onChildCheckedChanged($event)"
                    (onUpdateHeaderCheckboxStatus)="updateHeaderCheckboxStatus()"
                    [isChild]="true"
                    [parentIsHeader]="isHeader(item)"
                    />
                }
              </div>
            }`,
    styleUrls: ['./fi-checkbox2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkbox' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiCheckbox2Component,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiCheckbox2Component,
        },
    ],
    standalone: false,
})
export class FiCheckbox2Component extends ControlValueAccessorValidatorBase<number[]> implements OnInit, OnChanges {
    @Input() item: IFiCheckboxItem;
    @Input() disableIds: number[]; // todo obsolete, use isCheckboxDisabledFn for flexibility
    @Input() isChild: boolean;
    @Input() parentIsHeader: boolean;

    // determine if we can select item in dropdown
    @Input() isCheckboxDisabledFn: FiCheckboxDisabledFn = () => false;

    // resolve promise with result = true for accepting changing checkbox state
    @Input() canChangeChecked: (data: IFiCheckBoxChangeEvent) => Promise<boolean> = () => {
        return Promise.resolve(true);
    };

    @Output() checkedChange = new EventEmitter<IFiCheckboxChangeEvent>();
    @Output() onUpdateHeaderCheckboxStatus = new EventEmitter<void>();
    @ViewChildren(forwardRef(() => FiCheckbox2Component)) childCheckboxes: QueryList<FiCheckbox2Component>;

    checked: boolean = false;
    disabled: boolean = false;
    indeterminate: boolean = false;

    private showChildren = false;

    constructor(private injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.showCheckedChildren();
    }

    private showCheckedChildren() {
        if (this.hasChildItems() && !this.isHeader(this.item) && this.isSomeChildrenChecked()) {
            this.showChildren = true;
            this.checked = this.isAllChildrenChecked();
            this.updateIsDeterminate();
        }
    }

    private isAllChildrenChecked(): boolean {
        return this.item.childItems.every((item: IFiCheckboxItem) => this.isChecked(item));
    }

    private isSomeChildrenChecked(): boolean {
        return this.item.childItems.some((item: IFiCheckboxItem) => this.isChecked(item));
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.ngModel;
        if (change) {
            this.updateIsChecked();
            this.updateIsDeterminate();
            this.updateIsDisabled();
        }
        change = changes.disableIds;
        if (change) {
            this.updateIsDisabled();
        }
    }

    isHeader(item: IFiCheckboxItem) {
        return item.header;
    }
    isShowChildren() {
        return this.showChildren || this.isHeader(this.item);
    }
    hasChildItems() {
        return this.item.childItems && this.item.childItems.length;
    }
    getTitle(item: IFiCheckboxItem) {
        return this.item.name;
    }

    onCheckedChange($event: MatCheckboxChange) {
        this.canChangeChecked({ id: this.item.id, selected: $event.checked, header: this.isHeader(this.item) }).then(
            (result) => {
                if (result) {
                    this.processCheckedChanged(this.item, $event.checked);
                    this.updateIsChecked();

                    this.changeCheckedStateForChildren(this.item.childItems, $event.checked);
                    this.refreshDataAndChildren();
                    this.doPropagateChanges();

                    // for proper displaying checkbox state when has disabled children
                    this.indeterminate = false;
                    setTimeout(() => {
                        this.updateIsDeterminate();
                    });
                } else {
                    $event.source.checked = this.checked;
                }
            },
        );
    }
    refreshDataAndChildren(markForCheck?: boolean) {
        if (this.childCheckboxes) {
            this.childCheckboxes.forEach((item) => item.refreshDataAndChildren(true));
        }
        if (markForCheck) {
            this.updateIsChecked();
            this.updateIsDeterminate();
            this.cdr.markForCheck();
        }
    }
    private changeCheckedStateForChildren(items: IFiCheckboxItem[], checked: boolean) {
        if (items?.length) {
            items.forEach((item) => {
                this.processCheckedChanged(item, checked, true);
                this.changeCheckedStateForChildren(item.childItems, checked);
            });
        }
    }
    onChildCheckedChanged($event: IFiCheckboxChangeEvent) {
        let isNewChecked = $event.checked || !!this.item.childItems.find((childItem) => this.isChecked(childItem));
        this.processCheckedChanged(this.item, isNewChecked);
        this.updateIsChecked();
        this.updateIsDeterminate();
        this.doPropagateChanges();
    }
    private processCheckedChanged(item: IFiCheckboxItem, checked: boolean, skipCheckedChangeEvent?: boolean) {
        if (!this.isDisabled(item) && !this.isHeader(item)) {
            const idx = this.ngModel.indexOf(item.id);
            if (checked) {
                if (idx === -1) {
                    this.ngModel.push(item.id);
                }
            } else {
                if (idx !== -1) {
                    this.ngModel.splice(idx, 1);
                }
            }
        }
        if (!skipCheckedChangeEvent) {
            this.checkedChange.emit({
                id: item.id,
                checked: checked,
            });
        }
    }
    checkboxToggleClicked() {
        this.showChildren = !this.showChildren;
    }
    doPropagateChanges() {
        if (!this.isChild) {
            // propagate changes only if checkbox is on top
            super.doPropagateChanges();
        }
    }

    private updateIsChecked() {
        this.checked = this.isChecked(this.item);
    }
    private isChecked(item: IFiCheckboxItem) {
        if (this.isHeader(item)) return !!this.ngModel.length;
        return this.ngModel.indexOf(item.id) !== -1;
    }

    private updateIsDisabled() {
        this.disabled = this.isDisabled(this.item);
    }
    private isDisabled(item: IFiCheckboxItem) {
        // obsolete, use isCheckboxDisabledFn
        if (isDefined(this.disableIds) && this.disableIds.indexOf(item.id) !== -1)
            // item is disabled itself
            return true;

        if (this.isCheckboxDisabledFn(item)) {
            // item is disabled itself
            return true;
        }

        if (item.childItems) {
            // check if all children are disabled
            return !item.childItems.find((childItem) => !this.isDisabled(childItem));
        }
        return false;
    }

    private updateIsDeterminate() {
        this.indeterminate = this.isIndeterminate(this.item);
        this.onUpdateHeaderCheckboxStatus.emit();
    }

    updateHeaderCheckboxStatus() {
        this.updateIsChecked();
        this.updateIsDeterminate();
    }

    private isIndeterminate(item: IFiCheckboxItem) {
        const allChildren = [];
        const addChildren = (childItems: IFiCheckboxItem[]) => {
            if (childItems) {
                childItems.forEach((childItem) => {
                    allChildren.push(childItem);
                    addChildren(childItem.childItems);
                });
            }
        };
        addChildren(item.childItems);
        const hasCheckedChildren = !!allChildren.find((childItem) => this.isChecked(childItem));
        const hasUncheckedChildren = !!allChildren.find((childItem) => !this.isChecked(childItem));
        return hasCheckedChildren && hasUncheckedChildren;
    }
}
